import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { EmailTemplate } from '../types';

// Loading Skeleton Card
const SkeletonCard: React.FC = () => (
  <div style={{ 
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e5e7eb'
  }}>
    <div style={{ height: '20px', background: '#e5e7eb', borderRadius: '4px', width: '70%', marginBottom: '12px' }} />
    <div style={{ height: '14px', background: '#e5e7eb', borderRadius: '4px', width: '90%', marginBottom: '8px' }} />
    <div style={{ height: '14px', background: '#e5e7eb', borderRadius: '4px', width: '50%', marginBottom: '16px' }} />
    <div style={{ display: 'flex', gap: '8px' }}>
      <div style={{ height: '32px', background: '#e5e7eb', borderRadius: '6px', width: '60px' }} />
      <div style={{ height: '32px', background: '#e5e7eb', borderRadius: '6px', width: '60px' }} />
    </div>
  </div>
);

const Templates: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    html_body: '',
    category: 'general',
  });
  const [isVisualEditor, setIsVisualEditor] = useState(true);
  const editorRef = React.useRef<HTMLDivElement>(null);

  const getPreviewHtml = (html: string) => {
    const sampleValues: Record<string, string> = {
      '{first_name}': 'Alex',
      '{last_name}': 'Morgan',
      '{company}': 'Acme Inc.',
      '{email}': 'alex@example.com',
    };

    let rendered = html;
    Object.entries(sampleValues).forEach(([key, value]) => {
      rendered = rendered.split(key).join(value);
    });

    return rendered;
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/templates');
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch templates', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingTemplate) {
        await api.put(`/templates/${editingTemplate.id}`, formData);
      } else {
        await api.post('/templates', formData);
      }
      setShowCreateModal(false);
      setEditingTemplate(null);
      setFormData({ name: '', subject: '', html_body: '', category: 'general' });
      fetchTemplates();
    } catch (error) {
      alert('Failed to save template');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      html_body: template.html_body,
      category: template.category,
    });
    setShowCreateModal(true);
  };

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      setFormData({ ...formData, html_body: editorRef.current.innerHTML });
    }
  };

  const handleEditorInput = () => {
    if (editorRef.current) {
      setFormData({ ...formData, html_body: editorRef.current.innerHTML });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await api.delete(`/templates/${id}`);
      fetchTemplates();
    } catch (error) {
      alert('Failed to delete template');
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'marketing': return { bg: '#fce7f3', text: '#be185d' };
      case 'newsletter': return { bg: '#dbeafe', text: '#1e40af' };
      case 'transactional': return { bg: '#d1fae5', text: '#065f46' };
      default: return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 700, color: '#111827', margin: '0 0 4px 0' }}>Email Templates</h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Create and manage reusable email templates</p>
        </div>
        <button 
          onClick={() => {
            setEditingTemplate(null);
            setFormData({ name: '', subject: '', html_body: '', category: 'general' });
            setShowCreateModal(true);
          }}
          style={{ 
            padding: '12px 20px', 
            background: '#3b82f6', 
            color: 'white', 
            border: 'none', 
            borderRadius: '8px', 
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span>+</span> Create Template
        </button>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : templates.length === 0 ? (
        <div style={{ 
          background: 'white', 
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
          padding: '48px 24px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🎨</div>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
            No templates yet
          </h3>
          <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '0.875rem' }}>
            Create your first email template to get started
          </p>
          <button 
            onClick={() => setShowCreateModal(true)}
            style={{ 
              padding: '10px 20px', 
              background: '#3b82f6', 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Create Template
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
          {templates.map((template) => {
            const catColors = getCategoryColor(template.category);
            return (
              <div key={template.id} style={{ 
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #e5e7eb',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                cursor: 'pointer'
              }}
              onClick={() => setPreviewTemplate(template)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
              }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#111827' }}>{template.name}</h3>
                  <span style={{ 
                    padding: '4px 10px', 
                    borderRadius: '9999px',
                    background: catColors.bg,
                    color: catColors.text,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'capitalize'
                  }}>
                    {template.category}
                  </span>
                </div>
                <p style={{ 
                  margin: '0 0 16px 0', 
                  fontSize: '0.875rem', 
                  color: '#6b7280',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  <strong>Subject:</strong> {template.subject}
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(template);
                    }}
                    style={{ 
                      padding: '8px 16px', 
                      background: '#eff6ff', 
                      color: '#3b82f6', 
                      border: 'none', 
                      borderRadius: '6px', 
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }}
                  >
                    Edit
                  </button>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(template.id);
                    }}
                    style={{ 
                      padding: '8px 16px', 
                      background: '#fef2f2', 
                      color: '#dc2626', 
                      border: 'none', 
                      borderRadius: '6px', 
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreateModal && (
        <div style={{ 
          position: 'fixed', 
          top: 0, left: 0, right: 0, bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '16px', 
            width: '100%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', margin: '0 0 24px 0' }}>
              {editingTemplate ? 'Edit Template' : 'Create Template'}
            </h2>
            <form onSubmit={handleSave}>
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="templateName" style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Template Name *
                </label>
                <input
                  id="templateName"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  placeholder="e.g., Welcome Email"
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="subjectLine" style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Subject Line *
                </label>
                <input
                  id="subjectLine"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  placeholder="e.g., Welcome {first_name}!"
                />
                <p style={{ margin: '4px 0 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                  Use {'{first_name}'}, {'{last_name}'}, {'{company}'}, {'{email}'} for personalization
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="categorySelect" style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Category
                </label>
                <select
                  id="categorySelect"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    background: 'white',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="general">General</option>
                  <option value="marketing">Marketing</option>
                  <option value="newsletter">Newsletter</option>
                  <option value="transactional">Transactional</option>
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label htmlFor="htmlBody" style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                    Email Body *
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => setIsVisualEditor(!isVisualEditor)}
                      style={{
                        padding: '4px 12px',
                        background: isVisualEditor ? '#3b82f6' : '#f3f4f6',
                        color: isVisualEditor ? 'white' : '#374151',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                    >
                      {isVisualEditor ? 'Visual Editor' : 'HTML Code'}
                    </button>
                  </div>
                </div>
                
                {isVisualEditor ? (
                  <>
                    <div style={{ marginBottom: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap', padding: '8px', background: '#f9fafb', borderRadius: '6px' }}>
                      <button
                        type="button"
                        onClick={() => execCommand('bold')}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                          padding: '4px 8px',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >B</button>
                      <button
                        type="button"
                        onClick={() => execCommand('italic')}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                          padding: '4px 8px',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          fontStyle: 'italic'
                        }}
                      >I</button>
                      <button
                        type="button"
                        onClick={() => execCommand('underline')}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                          padding: '4px 8px',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                      >U</button>
                      <button
                        type="button"
                        onClick={() => execCommand('strikeThrough')}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                          padding: '4px 8px',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          textDecoration: 'line-through'
                        }}
                      >S</button>
                      <div style={{ width: '1px', background: '#d1d5db', margin: '0 4px' }}></div>
                      <button
                        type="button"
                        onClick={() => execCommand('formatBlock', 'h1')}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                          padding: '4px 8px',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >H1</button>
                      <button
                        type="button"
                        onClick={() => execCommand('formatBlock', 'h2')}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                          padding: '4px 8px',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >H2</button>
                      <button
                        type="button"
                        onClick={() => execCommand('formatBlock', 'p')}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                          padding: '4px 8px',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >P</button>
                      <div style={{ width: '1px', background: '#d1d5db', margin: '0 4px' }}></div>
                      <button
                        type="button"
                        onClick={() => execCommand('justifyLeft')}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                          padding: '4px 8px',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >Left</button>
                      <button
                        type="button"
                        onClick={() => execCommand('justifyCenter')}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                          padding: '4px 8px',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >Center</button>
                      <button
                        type="button"
                        onClick={() => execCommand('justifyRight')}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                          padding: '4px 8px',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >Right</button>
                      <div style={{ width: '1px', background: '#d1d5db', margin: '0 4px' }}></div>
                      <button
                        type="button"
                        onClick={() => execCommand('insertUnorderedList')}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                          padding: '4px 8px',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >• List</button>
                      <button
                        type="button"
                        onClick={() => execCommand('insertOrderedList')}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                          padding: '4px 8px',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >1. List</button>
                      <div style={{ width: '1px', background: '#d1d5db', margin: '0 4px' }}></div>
                      <button
                        type="button"
                        onClick={() => {
                          const url = prompt('Enter URL:', 'http://');
                          if (url) execCommand('createLink', url);
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                          padding: '4px 8px',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >Link</button>
                    </div>
                    <div
                      ref={editorRef}
                      contentEditable
                      onInput={handleEditorInput}
                      dangerouslySetInnerHTML={{ __html: formData.html_body }}
                      style={{
                        minHeight: '200px',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        outline: 'none',
                        background: 'white',
                        overflow: 'auto'
                      }}
                    />
                  </>
                ) : (
                  <textarea
                    id="htmlBody"
                    required
                    value={formData.html_body}
                    onChange={(e) => setFormData({...formData, html_body: e.target.value})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      outline: 'none',
                      minHeight: '200px', 
                      fontFamily: 'monospace',
                      boxSizing: 'border-box'
                    }}
                    placeholder="<html><body><h1>Hello {first_name}</h1>...</body></html>"
                  />
                )}
                <p style={{ margin: '4px 0 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                  Use {'{first_name}'}, {'{last_name}'}, {'{company}'}, {'{email}'} for personalization
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  disabled={submitting}
                  style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: '#f3f4f6', 
                    color: '#374151',
                    border: 'none', 
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={submitting}
                  style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: submitting ? '#9ca3af' : '#3b82f6', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {submitting ? (
                    <>
                      <span style={{ 
                        width: '16px', 
                        height: '16px', 
                        border: '2px solid rgba(255,255,255,0.3)', 
                        borderTopColor: 'white', 
                        borderRadius: '50%', 
                        animation: 'spin 1s linear infinite',
                        display: 'inline-block'
                      }} />
                      Saving...
                    </>
                  ) : (
                    editingTemplate ? 'Update Template' : 'Create Template'
                  )}
                </button>
              </div>
            </form>

            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}

      {previewTemplate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '1000px',
            height: '85vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#111827' }}>
                  Preview: {previewTemplate.name}
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.8125rem', color: '#6b7280' }}>
                  Subject: {getPreviewHtml(previewTemplate.subject)}
                </p>
              </div>
              <button
                onClick={() => setPreviewTemplate(null)}
                style={{
                  padding: '8px 12px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#374151'
                }}
              >
                Close
              </button>
            </div>

            <div style={{ flex: 1, background: '#f9fafb', padding: '16px' }}>
              <iframe
                title={`Template preview ${previewTemplate.id}`}
                srcDoc={getPreviewHtml(previewTemplate.html_body)}
                style={{
                  width: '100%',
                  height: '100%',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  background: 'white'
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Templates;
